/**
 * Data for "2025 Engineering Benchmarks: Year in Review" research report
 * Source: GitHub Archive / BigQuery - October 2025
 *
 * NOTE: November-December 2025 GitHub Archive data has degraded payload structure
 * (missing user.login, additions, deletions, merged_by fields), so we use October 2025
 * as the most recent month with complete data. This is documented in the report.
 */

export const studyMeta2025 = {
  totalPRs: 802_979,
  uniqueRepos: 262_212,
  uniqueAuthors: 191_099,
  timeRange: 'October 2025',
  dataSource: 'GitHub Archive / BigQuery',
  title: '2025 Engineering Benchmarks: Year in Review',
  subtitle: 'What changed in how software ships - and what to watch in 2026',
  previousStudy: {
    totalPRs: 3_387_250,
    timeRange: 'December 2024',
    slug: 'code-review-study-2024',
  },
};

// ============================================================================
// REVIEWED PRs BENCHMARK - Enterprise-Relevant Metrics
// PRs that received at least one code review event (14.6% of all PRs)
// More representative of team-based development workflows
// ============================================================================

export const reviewedPRsMeta = {
  totalPRs: 117_413,
  uniqueRepos: 49_808,
  uniqueAuthors: 50_274,
  percentOfAllPRs: 14.6,
  description: 'PRs with at least one code review event',
};

// Cycle time breakdown for reviewed PRs (the real enterprise benchmark)
export const reviewedPRsCycleTime = {
  prCount: 117_413,
  // Waiting for Review (PR opened → first review)
  waiting: {
    avgHours: 96.9,
    medianHours: 0.6,
    p90Hours: 128.9,
  },
  // In Review (first review → approval)
  review: {
    avgHours: 4.8,
    medianHours: 0.0,
    p90Hours: 9.4,
  },
  // Merge Delay (approval → merged)
  merge: {
    avgHours: 6.9,
    medianHours: 0.1,
    p90Hours: 19.6,
  },
  // Total cycle time
  total: {
    avgHours: 105.0,
    medianHours: 3.0,
    p90Hours: 148.9,
  },
};

// Self-merge rate among reviewed PRs
export const reviewedPRsSelfMerge = {
  totalMerged: 117_390,
  selfMerged: 61_169,
  mergedByOther: 56_221,
  selfMergePercent: 52.11,
  // Compare to all PRs
  allPRsSelfMergePercent: 71.48,
  difference: -19.37, // reviewed PRs have 19pp lower self-merge
};

// Cycle time by PR size for reviewed PRs
export const reviewedPRsBySizeCycleTime = [
  { bucket: 'Tiny (<10)', prCount: 31491, medianWaitHours: 0.2, medianTotalHours: 1.1 },
  { bucket: 'Small (10-50)', prCount: 25754, medianWaitHours: 0.5, medianTotalHours: 2.6 },
  { bucket: 'Medium (50-200)', prCount: 23192, medianWaitHours: 0.9, medianTotalHours: 4.7 },
  { bucket: 'Large (200-500)', prCount: 14465, medianWaitHours: 0.9, medianTotalHours: 5.8 },
  { bucket: 'XL (500-1000)', prCount: 8356, medianWaitHours: 1.1, medianTotalHours: 8.7 },
  { bucket: 'Massive (1000+)', prCount: 14155, medianWaitHours: 0.8, medianTotalHours: 7.3 },
];

// First-time vs repeat contributors for reviewed PRs
export const reviewedPRsContributorData = {
  firstTime: {
    prCount: 38_993,
    avgHours: 188.0,
    medianHours: 15.2,
    p90Hours: 295.4,
  },
  repeat: {
    prCount: 78_420,
    avgHours: 63.7,
    medianHours: 1.4,
    p90Hours: 94.4,
  },
  waitPenaltyMultiplier: 10.9, // First-timers wait 10.9x longer
  // Compare to all PRs penalty
  allPRsWaitPenaltyPercent: 37.5,
};

// Review quality among reviewed PRs
export const reviewedPRsQuality = {
  totalPRs: 117_413,
  avgReviewComments: 2.31,
  medianReviewComments: 0,
  zeroCommentPRs: 79_560,
  prsWithComments: 37_853,
  pctWithComments: 32.2,
  // 68% of "reviewed" PRs have no actual comments - just approvals
};

// Day of week for reviewed PRs
export const reviewedPRsDayOfWeek = [
  { dayOfWeek: 1, dayName: 'Sunday', merges: 9908, percentage: 8.44 },
  { dayOfWeek: 2, dayName: 'Monday', merges: 17508, percentage: 14.91 },
  { dayOfWeek: 3, dayName: 'Tuesday', merges: 18768, percentage: 15.98 },
  { dayOfWeek: 4, dayName: 'Wednesday', merges: 27850, percentage: 23.72 },
  { dayOfWeek: 5, dayName: 'Thursday', merges: 16859, percentage: 14.36 },
  { dayOfWeek: 6, dayName: 'Friday', merges: 16253, percentage: 13.84 },
  { dayOfWeek: 7, dayName: 'Saturday', merges: 10267, percentage: 8.74 },
];

// Weekend percentage comparison
export const reviewedPRsWeekendWork = {
  reviewedPRsWeekendPercent: 17.18, // Sat + Sun
  allPRsWeekendPercent: 21.49,
  difference: -4.31, // Reviewed PRs have less weekend work
};

// ============================================================================
// GLOBAL vs ENTERPRISE BENCHMARK COMPARISON
// Side-by-side comparison for easy reference
// ============================================================================

export const benchmarkComparison = {
  global: {
    label: 'All GitHub PRs',
    description: 'Includes solo projects, hobby repos, self-merged code',
    totalPRs: 802_979,
    medianCycleTimeHours: 0, // Most merge instantly
    selfMergePercent: 71.48,
    weekendMergePercent: 21.49,
    pctWithReview: 14.6,
  },
  enterprise: {
    label: 'Reviewed PRs Only',
    description: 'PRs with code review - more representative of team workflows',
    totalPRs: 117_413,
    medianCycleTimeHours: 3.0,
    selfMergePercent: 52.11,
    weekendMergePercent: 17.18,
    pctWithComments: 32.2,
  },
};

// Query A + E: Review rates by PR size
export const reviewBySize2025 = [
  {
    bucket: 'Tiny (<10 lines)',
    shortLabel: '<10',
    prCount: 214830,
    pctNoReview: 85.8,
    pctZeroReviewComments: 98.3,
    avgReviewComments: 0.03,
    commentsPerLine: 0.98,
    avgApprovals: 0.14,
  },
  {
    bucket: 'Small (10-50)',
    shortLabel: '10-50',
    prCount: 163088,
    pctNoReview: 84.2,
    pctZeroReviewComments: 94.8,
    avgReviewComments: 0.15,
    commentsPerLine: 0.66,
    avgApprovals: 0.14,
  },
  {
    bucket: 'Medium (50-200)',
    shortLabel: '50-200',
    prCount: 151472,
    pctNoReview: 84.7,
    pctZeroReviewComments: 91.6,
    avgReviewComments: 0.4,
    commentsPerLine: 0.39,
    avgApprovals: 0.12,
  },
  {
    bucket: 'Large (200-500)',
    shortLabel: '200-500',
    prCount: 93467,
    pctNoReview: 84.6,
    pctZeroReviewComments: 89.6,
    avgReviewComments: 0.68,
    commentsPerLine: 0.22,
    avgApprovals: 0.11,
  },
  {
    bucket: 'XL (500-1000)',
    shortLabel: '500-1K',
    prCount: 55948,
    pctNoReview: 85.1,
    pctZeroReviewComments: 89.0,
    avgReviewComments: 0.94,
    commentsPerLine: 0.14,
    avgApprovals: 0.1,
  },
  {
    bucket: 'Massive (1000+)',
    shortLabel: '1000+',
    prCount: 109361,
    pctNoReview: 87.0,
    pctZeroReviewComments: 90.3,
    avgReviewComments: 1.17,
    commentsPerLine: 0.05,
    avgApprovals: 0.08,
  },
];

// Query D: Self-merge statistics
export const selfMergeData2025 = {
  selfMerged: 573883,
  mergedByOther: 228935,
  selfMergePercent: 71.48,
  otherMergePercent: 28.52,
  // YoY comparison
  previousYear: {
    selfMergePercent: 68.03,
    change: 3.45, // percentage points increase
  },
};

// Query B: First-time vs repeat contributor data
export const contributorData2025 = {
  firstTime: {
    label: 'First-time contributor',
    prCount: 21289,
    avgHours: 86.5,
    medianHours: 22.0,
    p90Hours: 280.0,
  },
  repeat: {
    label: 'Repeat contributor',
    prCount: 171251,
    avgHours: 62.8,
    medianHours: 16.0,
    p90Hours: 175.0,
  },
  waitPenaltyPercent: 37.5, // (22-16)/16 * 100
  p90PenaltyPercent: 60, // (280-175)/175 * 100
  // YoY comparison
  previousYear: {
    waitPenaltyPercent: 53,
    change: -15.5, // improvement
  },
};

// Query G: Day of week merge distribution
export const mergeDayData2025 = [
  { dayOfWeek: 1, dayName: 'Sunday', merges: 84327, percentage: 10.5 },
  { dayOfWeek: 2, dayName: 'Monday', merges: 111703, percentage: 13.91 },
  { dayOfWeek: 3, dayName: 'Tuesday', merges: 115098, percentage: 14.33 },
  { dayOfWeek: 4, dayName: 'Wednesday', merges: 189096, percentage: 23.55 },
  { dayOfWeek: 5, dayName: 'Thursday', merges: 109198, percentage: 13.6 },
  { dayOfWeek: 6, dayName: 'Friday', merges: 105311, percentage: 13.12 },
  { dayOfWeek: 7, dayName: 'Saturday', merges: 88246, percentage: 10.99 },
];

// Query F: Time of day merge distribution
export const mergeHourData2025 = [
  { hour: 0, mergeCount: 31565, percentage: 3.93 },
  { hour: 1, mergeCount: 28595, percentage: 3.56 },
  { hour: 2, mergeCount: 30467, percentage: 3.79 },
  { hour: 3, mergeCount: 28442, percentage: 3.54 },
  { hour: 4, mergeCount: 29747, percentage: 3.7 },
  { hour: 5, mergeCount: 30341, percentage: 3.78 },
  { hour: 6, mergeCount: 32253, percentage: 4.02 },
  { hour: 7, mergeCount: 34696, percentage: 4.32 },
  { hour: 8, mergeCount: 36215, percentage: 4.51 },
  { hour: 9, mergeCount: 35835, percentage: 4.46 },
  { hour: 10, mergeCount: 35336, percentage: 4.4 },
  { hour: 11, mergeCount: 35896, percentage: 4.47 },
  { hour: 12, mergeCount: 32732, percentage: 4.08 },
  { hour: 13, mergeCount: 36880, percentage: 4.59 },
  { hour: 14, mergeCount: 39018, percentage: 4.86 },
  { hour: 15, mergeCount: 39888, percentage: 4.97 },
  { hour: 16, mergeCount: 39566, percentage: 4.93 },
  { hour: 17, mergeCount: 33873, percentage: 4.22 },
  { hour: 18, mergeCount: 32800, percentage: 4.08 },
  { hour: 19, mergeCount: 33684, percentage: 4.19 },
  { hour: 20, mergeCount: 34143, percentage: 4.25 },
  { hour: 21, mergeCount: 32912, percentage: 4.1 },
  { hour: 22, mergeCount: 30123, percentage: 3.75 },
  { hour: 23, mergeCount: 27972, percentage: 3.48 },
];

// Query H: Time to merge buckets
export const timeToMergeData2025 = [
  { bucket: 'Same day (<24h)', prCount: 122532, percentage: 55.27 },
  { bucket: '1-3 days', prCount: 36269, percentage: 16.36 },
  { bucket: '3-7 days', prCount: 25626, percentage: 11.56 },
  { bucket: '1-2 weeks', prCount: 13725, percentage: 6.19 },
  { bucket: '2-4 weeks', prCount: 10817, percentage: 4.88 },
  { bucket: 'Over 1 month', prCount: 12737, percentage: 5.74 },
];

// Query 4: PR Size vs Merge Time
export const sizeVsMergeTime2025 = [
  { bucket: 'Tiny (<10 lines)', prCount: 62735, avgHours: 63.2, medianHours: 13.0 },
  { bucket: 'Small (10-50)', prCount: 46768, avgHours: 60.8, medianHours: 16.0 },
  { bucket: 'Medium (50-200)', prCount: 38237, avgHours: 67.0, medianHours: 19.0 },
  { bucket: 'Large (200-500)', prCount: 22795, avgHours: 72.0, medianHours: 20.0 },
  { bucket: 'XL (500-1000)', prCount: 13126, avgHours: 78.2, medianHours: 22.0 },
  { bucket: 'Massive (1000+)', prCount: 24031, avgHours: 81.4, medianHours: 22.0 },
];

// Query C: Language statistics (top 25)
export const languageData2025 = [
  { language: 'TypeScript', mergedPRs: 43177, avgHours: 59.5, medianHours: 15.0, avgPRSize: 503 },
  { language: 'Python', mergedPRs: 29761, avgHours: 69.6, medianHours: 17.0, avgPRSize: 371 },
  { language: 'JavaScript', mergedPRs: 16904, avgHours: 61.6, medianHours: 15.0, avgPRSize: 461 },
  { language: 'Java', mergedPRs: 14940, avgHours: 71.5, medianHours: 20.0, avgPRSize: 287 },
  { language: 'Go', mergedPRs: 13726, avgHours: 80.0, medianHours: 19.0, avgPRSize: 284 },
  { language: 'Rust', mergedPRs: 8374, avgHours: 68.0, medianHours: 19.0, avgPRSize: 404 },
  { language: 'C++', mergedPRs: 7187, avgHours: 73.9, medianHours: 21.0, avgPRSize: 352 },
  { language: 'C#', mergedPRs: 6762, avgHours: 62.2, medianHours: 16.0, avgPRSize: 463 },
  { language: 'HTML', mergedPRs: 5903, avgHours: 62.3, medianHours: 16.0, avgPRSize: 447 },
  { language: 'Shell', mergedPRs: 5230, avgHours: 64.4, medianHours: 14.0, avgPRSize: 148 },
  { language: 'Kotlin', mergedPRs: 4478, avgHours: 70.0, medianHours: 19.0, avgPRSize: 309 },
  { language: 'PHP', mergedPRs: 4154, avgHours: 71.4, medianHours: 20.0, avgPRSize: 370 },
  { language: 'Ruby', mergedPRs: 3534, avgHours: 66.8, medianHours: 15.0, avgPRSize: 174 },
  { language: 'C', mergedPRs: 2844, avgHours: 80.7, medianHours: 24.0, avgPRSize: 337 },
  { language: 'Jupyter Notebook', mergedPRs: 1759, avgHours: 70.6, medianHours: 19.0, avgPRSize: 774 },
  { language: 'Vue', mergedPRs: 1575, avgHours: 70.7, medianHours: 17.0, avgPRSize: 526 },
  { language: 'HCL', mergedPRs: 1492, avgHours: 56.5, medianHours: 15.0, avgPRSize: 133 },
  { language: 'Scala', mergedPRs: 1466, avgHours: 65.9, medianHours: 17.0, avgPRSize: 139 },
  { language: 'YAML', mergedPRs: 1416, avgHours: 74.7, medianHours: 15.0, avgPRSize: 29 },
  { language: 'CSS', mergedPRs: 1397, avgHours: 59.2, medianHours: 17.0, avgPRSize: 496 },
  { language: 'Nix', mergedPRs: 1366, avgHours: 61.1, medianHours: 14.0, avgPRSize: 77 },
  { language: 'PowerShell', mergedPRs: 1292, avgHours: 33.2, medianHours: 4.0, avgPRSize: 234 },
  { language: 'Dockerfile', mergedPRs: 1174, avgHours: 51.6, medianHours: 11.0, avgPRSize: 110 },
  { language: 'Dart', mergedPRs: 1105, avgHours: 72.6, medianHours: 19.0, avgPRSize: 844 },
  { language: 'Swift', mergedPRs: 1098, avgHours: 73.5, medianHours: 23.0, avgPRSize: 569 },
];

// Hero statistics for the report
export const heroStats2025 = [
  {
    value: '90%',
    label: 'No Review',
    sublabel: '(1000+ line PRs)',
    description: '90% of massive PRs ship without formal code review',
    change: '+7pp vs 2024',
  },
  {
    value: '71%',
    label: 'Self-Merged',
    sublabel: '(all PRs)',
    description: 'Nearly three-quarters of code merges without a second pair of eyes',
    change: '+3pp vs 2024',
  },
  {
    value: '20x',
    label: 'Less Scrutiny',
    sublabel: '(large PRs)',
    description: 'Review comments per 100 lines drops from 0.98 to 0.05',
    change: 'Similar to 2024',
  },
  {
    value: '38%',
    label: 'Longer Wait',
    sublabel: '(new contributors)',
    description: 'First-time contributors still wait significantly longer',
    change: 'Improved from 53%',
  },
];

// Bot activity analysis
export const botActivityData2025 = {
  october2025: {
    botPRs: 475905,
    humanPRs: 2594901,
    botPercent: 15.5,
    humanPercent: 84.5,
  },
  yearOverYear: [
    { year: 2020, botPRs: 25581352, humanPRs: 21466432, botPercent: 54.4, totalPRs: 47047784 },
    { year: 2021, botPRs: 33179666, humanPRs: 24921260, botPercent: 57.1, totalPRs: 58100926 },
    { year: 2022, botPRs: 47152116, humanPRs: 28871212, botPercent: 62.0, totalPRs: 76023328 },
    { year: 2023, botPRs: 22739143, humanPRs: 33519640, botPercent: 40.4, totalPRs: 56258783 },
    { year: 2024, botPRs: 19646468, humanPRs: 37593372, botPercent: 34.3, totalPRs: 57239840 },
    { year: 2025, botPRs: 16717565, humanPRs: 36338983, botPercent: 31.5, totalPRs: 53056548 },
  ],
  peakYear: 2022,
  peakPercent: 62.0,
  currentPercent: 31.5,
  declineFromPeak: 30.5, // percentage points
};

// Weekend and work pattern analysis
export const workPatternData2025 = {
  weekendPushPercent: 25.37, // Sun 12.77 + Sat 12.6
  weekdayPushPercent: 74.63,
  pushByDay: [
    { day: 'Sunday', percent: 12.77 },
    { day: 'Monday', percent: 11.8 },
    { day: 'Tuesday', percent: 12.73 },
    { day: 'Wednesday', percent: 18.52 },
    { day: 'Thursday', percent: 15.83 },
    { day: 'Friday', percent: 15.75 },
    { day: 'Saturday', percent: 12.6 },
  ],
  peakMergeDay: 'Wednesday',
  peakMergeDayPercent: 23.55,
  fridayMergePercent: 13.12,
  // YoY comparison
  previousYear: {
    peakMergeDay: 'Monday',
    peakMergeDayPercent: 19.08,
  },
};

// Key changes from 2024 to 2025
export const yoyChanges = {
  selfMergeRate: {
    y2024: 68.03,
    y2025: 71.48,
    change: 3.45,
    direction: 'up' as const,
    interpretation: 'More code shipping without peer review',
  },
  botPRRate: {
    y2024: 37.87,
    y2025: 15.5,
    change: -22.37,
    direction: 'down' as const,
    interpretation: 'Dramatic decline in automated PRs',
  },
  noReviewMassivePRs: {
    y2024: 82.6,
    y2025: 90.3,
    change: 7.7,
    direction: 'up' as const,
    interpretation: 'Large PRs getting even less scrutiny',
  },
  firstContributorWait: {
    y2024: 53,
    y2025: 37.5,
    change: -15.5,
    direction: 'down' as const,
    interpretation: 'Onboarding experience improving',
  },
  sameDayMerge: {
    y2024: 52.67,
    y2025: 55.27,
    change: 2.6,
    direction: 'up' as const,
    interpretation: 'Faster merge cycles',
  },
  peakMergeDay: {
    y2024: 'Monday',
    y2025: 'Wednesday',
    interpretation: 'Merge patterns shifted mid-week',
  },
};

// GitHub Archive data quality finding
export const dataQualityFinding = {
  title: 'GitHub Archive Data Quality Alert',
  description:
    'Starting November 2025, GitHub Archive event payloads have significantly reduced detail. Fields including user.login, additions, deletions, merged_by, and review_comments are no longer populated.',
  affectedMonths: ['November 2025', 'December 2025'],
  lastGoodMonth: 'October 2025',
  impact:
    'Many PR metrics (self-merge rate, PR size analysis, contributor analysis) cannot be calculated from November 2025 onwards.',
  recommendation:
    'Teams relying on GitHub Archive for analytics should monitor data quality and consider alternative data sources.',
};

// ============================================================================
// AI Developer Tools Spotlight Data
// Source: GitHub Archive / BigQuery - January-October 2025
// Repos analyzed: openai/codex, google-gemini/gemini-cli, anthropics/claude-code
// ============================================================================

export const aiReposMeta = {
  timeRange: 'January - October 2025',
  dataSource: 'GitHub Archive / BigQuery',
  note: 'Time range extended to capture full activity for newer repos',
};

export const aiReposData = [
  {
    name: 'openai/codex',
    displayName: 'OpenAI Codex',
    // Overall stats
    mergedPRs: 1031,
    uniqueAuthors: 453,
    firstEvent: '2025-04-16',
    lastEvent: '2025-10-31',
    // Cycle time
    avgHoursToMerge: 32.5,
    medianHoursToMerge: 11,
    p90HoursToMerge: 73,
    // PR size
    avgPRSize: 287,
    medianPRSize: 54,
    // Review quality
    selfMergeRate: 73.52,
    avgReviewComments: 1.72,
    pctNoReviewComments: 73.62,
    // Time to first review
    avgHoursToFirstReview: 18.8,
    medianHoursToFirstReview: 1,
    // Bot activity
    botPRPercent: 2.91,
    // Weekend work
    weekendMergePercent: 14.1, // (58+87)/1031
  },
  {
    name: 'google-gemini/gemini-cli',
    displayName: 'Gemini CLI',
    // Overall stats
    mergedPRs: 948,
    uniqueAuthors: 523,
    firstEvent: '2025-06-25',
    lastEvent: '2025-10-31',
    // Cycle time
    avgHoursToMerge: 98.5,
    medianHoursToMerge: 21,
    p90HoursToMerge: 217,
    // PR size
    avgPRSize: 245,
    medianPRSize: 74,
    // Review quality
    selfMergeRate: 58.44,
    avgReviewComments: 3.91,
    pctNoReviewComments: 35.13,
    // Time to first review
    avgHoursToFirstReview: 7,
    medianHoursToFirstReview: 0,
    // Bot activity
    botPRPercent: 2.43,
    // Weekend work
    weekendMergePercent: 14.0, // (50+83)/948
  },
  {
    name: 'anthropics/claude-code',
    displayName: 'Claude Code',
    // Overall stats
    mergedPRs: 54,
    uniqueAuthors: 53,
    firstEvent: '2025-02-24',
    lastEvent: '2025-10-31',
    // Cycle time
    avgHoursToMerge: 191,
    medianHoursToMerge: 100,
    p90HoursToMerge: 514,
    // PR size
    avgPRSize: 65,
    medianPRSize: 18,
    // Review quality
    selfMergeRate: 68.52,
    avgReviewComments: 0.43,
    pctNoReviewComments: 81.48,
    // Time to first review
    avgHoursToFirstReview: 54.7,
    medianHoursToFirstReview: 0,
    // Bot activity
    botPRPercent: 0,
    // Weekend work
    weekendMergePercent: 7.4, // (4)/54 - only Saturday data visible
  },
];

// AI Repos Cycle Time Breakdown (3-phase: Waiting → Review → Merge)
export const aiReposCycleTimeBreakdown = [
  {
    name: 'openai/codex',
    displayName: 'OpenAI Codex',
    prCount: 1031,
    prsWithReview: 546,
    pctWithReview: 53,
    // Phase breakdown (hours)
    waiting: { avg: 13.7, median: 1.4 },
    review: { avg: 11.7, median: 0.0 },
    merge: { avg: 10.5, median: 0.5 },
    total: { avg: 19.6, median: 2.3 },
  },
  {
    name: 'google-gemini/gemini-cli',
    displayName: 'Gemini CLI',
    prCount: 948,
    prsWithReview: 820,
    pctWithReview: 86,
    // Phase breakdown (hours)
    waiting: { avg: 8.8, median: 0.0 },
    review: { avg: 55.9, median: 2.6 },
    merge: { avg: 36.4, median: 1.1 },
    total: { avg: 73.8, median: 7.8 },
  },
  {
    name: 'anthropics/claude-code',
    displayName: 'Claude Code',
    prCount: 54,
    prsWithReview: 40,
    pctWithReview: 74,
    // Phase breakdown (hours)
    waiting: { avg: 57.2, median: 0.3 },
    review: { avg: 33.4, median: 0.0 },
    merge: { avg: 60.5, median: 0.1 },
    total: { avg: 115.1, median: 0.9 },
  },
];

// AI Repos Cycle Time by PR Size
export const aiReposCycleTimeBySize = {
  codex: [
    { size: 'Tiny (<10)', prCount: 225, medianTotal: 0.7 },
    { size: 'Small (10-50)', prCount: 273, medianTotal: 1.1 },
    { size: 'Medium (50-200)', prCount: 281, medianTotal: 2.5 },
    { size: 'Large (200-500)', prCount: 144, medianTotal: 7.5 },
    { size: 'XL (500+)', prCount: 108, medianTotal: 14.0 },
  ],
  gemini: [
    { size: 'Tiny (<10)', prCount: 186, medianTotal: 2.2 },
    { size: 'Small (10-50)', prCount: 215, medianTotal: 2.5 },
    { size: 'Medium (50-200)', prCount: 268, medianTotal: 7.5 },
    { size: 'Large (200-500)', prCount: 171, medianTotal: 22.2 },
    { size: 'XL (500+)', prCount: 108, medianTotal: 50.2 },
  ],
  claude: [
    { size: 'Tiny (<10)', prCount: 25, medianTotal: 3.4 },
    { size: 'Small (10-50)', prCount: 15, medianTotal: 0.1 },
    { size: 'Medium (50-200)', prCount: 10, medianTotal: 0.3 },
    { size: 'Large (200-500)', prCount: 2, medianTotal: 17.4 },
    { size: 'XL (500+)', prCount: 2, medianTotal: 0.1 },
  ],
};

// GitHub benchmarks - now with BOTH global and reviewed PRs for comparison
export const aiReposGitHubBenchmarks = {
  // Global benchmarks (all PRs - includes solo/hobby projects)
  global: {
    label: 'All GitHub PRs',
    medianHoursToMerge: 0, // Most merge instantly (no review)
    selfMergeRate: 71.48,
    avgPRSize: 200,
    botPRPercent: 15.5,
    weekendMergePercent: 21.5,
    pctNoReviewComments: 85,
    pctWithReview: 14.6,
  },
  // Enterprise benchmarks (reviewed PRs only - team workflows)
  reviewed: {
    label: 'Reviewed PRs Only',
    medianHoursToMerge: 3.0,
    selfMergeRate: 52.11,
    avgPRSize: 54, // median
    botPRPercent: 15.5, // similar
    weekendMergePercent: 17.18,
    pctNoReviewComments: 67.8, // 100 - 32.2% with comments
    pctWithReview: 100, // by definition
  },
};

// Key insights comparing AI repos to GitHub benchmarks
export const aiReposInsights = [
  {
    title: 'Codex: Faster Than Enterprise Teams',
    stat: '2.3h',
    comparison: 'vs 3h reviewed PR benchmark',
    description: 'OpenAI ships 23% faster than typical team workflows',
  },
  {
    title: 'Gemini: Exceptional Review Culture',
    stat: '86%',
    comparison: 'vs 14.6% GitHub average',
    description: '86% of Gemini PRs go through review (6x the GitHub rate)',
  },
  {
    title: 'AI Tools Are Human-Driven',
    stat: '<3%',
    comparison: 'vs 15.5% GitHub average',
    description: 'Bot PRs nearly absent - these are human-crafted projects',
  },
  {
    title: 'Gemini Reviews Are Thorough',
    stat: '65%',
    comparison: 'vs 32% reviewed PR benchmark',
    description: '65% of Gemini PRs have review comments (2x typical)',
  },
];

export const aiReposQuotables = [
  'OpenAI Codex ships in 2.3 hours median - 23% faster than the 3-hour enterprise benchmark.',
  'Gemini CLI has 86% review engagement vs 14.6% GitHub average - 6x the rate of typical open source.',
  'Bot PRs are nearly absent in AI developer tools (<3%), while GitHub overall sits at 15.5%.',
  'AI tool teams work fewer weekends: 14% weekend merges vs 17% for enterprise, 21% for all GitHub.',
  'Claude Code ships the smallest PRs: 18 lines median vs 54 lines for reviewed enterprise PRs.',
  'The 3-hour median cycle time is the real enterprise benchmark - not GitHub\'s 0h (which reflects instant self-merges).',
];

// Key quotables for press/social
export const quotables2025 = [
  '90% of pull requests over 1,000 lines ship without any code review - up from 83% last year.',
  'Self-merge rates hit 71%, meaning nearly three-quarters of code gets no peer review before shipping.',
  'Bot PRs collapsed from 62% (2022 peak) to just 15.5% in 2025. The automation boom is over.',
  'Wednesday is the new Monday: 23.5% of PRs now merge mid-week, shifting from the traditional Monday peak.',
  'Good news for newcomers: first-time contributor wait times dropped from 53% to 38% longer than veterans.',
  'PowerShell remains the speed king at 4 hours median merge time - 6x faster than C at 24 hours.',
  "GitHub Archive data quality degraded in late 2025 - a warning sign for the analytics ecosystem.",
  'Same-day merges hit 55%, up from 53%. Code is shipping faster than ever.',
];
